import type { ReactNode } from 'react';

import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value: string[];
  readComponent?: ReactNode;
  onChange?: (value?: string[]) => void;
  inputWidth?: number | string;
  inputClassName?: string;
};

/**
 * Card-based Form / MultiTextItem
 */
export const CFormMultiTextItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();

  const handleAddNew = () => {
    props.onChange?.(props.value.concat(['']));
  };
  const handleRemove = (index: number) => {
    props.onChange?.(props.value.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, value: string) => {
    props.onChange?.(props.value.map((v, i) => (i === index ? value : v)));
  };

  return (
    <CFormItemBase
      {...props}
      readComponent={
        props.readComponent ?? (
          <VStack className="gap-2 items-start">
            {props.value.map((v, index) => (
              <div key={index}>{v}</div>
            ))}
          </VStack>
        )
      }
      writeComponent={
        <VStack className="gap-2 items-start">
          {props.value.map((v, index) => (
            <HStack key={index} className="gap-2 items-center">
              <Input
                value={v}
                onChange={(e) => handleChange(index, e.target.value)}
                width={props.inputWidth}
                className={props.inputClassName}
                disabled={props.readonly}
              />
              <Button
                variant="ghost"
                className="p-2 w-8 h-8"
                onClick={() => handleRemove(index)}
              >
                <Icon iconName="Trash2Icon" />
              </Button>
            </HStack>
          ))}
          <Button size="sm" onClick={handleAddNew}>
            {tc('action.add')}
          </Button>
        </VStack>
      }
    />
  );
});
